import { type ObjectId, RenderStateClippingPlane } from "@novorender/api";
import type { BoundingSphere, ObjectGroup, SceneData } from "@novorender/data-js-api";
import type { Camera, Internal } from "@novorender/webgl-api";
import { quat, ReadonlyVec3, vec3 } from "gl-matrix";

import { type DitioMachine } from "features/ditio";
import { Checklist } from "features/ditio/types";
import type { LogPoint, MachineLocation } from "features/xsiteManage";
import type { CustomProperties } from "types/project";
import type { VecRGB, VecRGBA } from "utils/color";

export enum CameraSpeedLevel {
    Slow = "slow",
    Default = "default",
    Fast = "fast",
}

export enum ObjectVisibility {
    Neutral = "neutral",
    SemiTransparent = "semiTransparent",
    Transparent = "transparent",
    Xray = "xray",
}

export enum CameraType {
    Orthographic,
    Pinhole,
}

export enum SubtreeStatus {
    Unavailable = "unavailable",
    Shown = "shown",
    Hidden = "hidden",
}

export enum SelectionBasketMode {
    Loose,
    Strict,
}

export enum SelectionResolution {
    FirstObject = "firstObject",
    LastObject = "lastObject",
}

export enum Picker {
    Object,
    Measurement,
    FollowPathObject,
    ClippingPlane,
    ClippingBox,
    OrthoPlane,
    CrossSection,
    Area,
    PointLine,
    HeightProfileEntity,
    Manhole,
    OutlineLaser,
    FormLocation,
    JiraIssuePosition,
    Coordinates,
    Dimension,
    Pins,
    SectionPlane,
    SdkPicking,
    /** Pick two whole objects and measure the shortest line between them. */
    ObjectMeasurement,
}

/**
 * Visibility of each geometry kind in the scene. A kind the loaded scene does not contain
 * is `Unavailable` rather than `Hidden`.
 */
export type Subtrees = {
    /** Meshed surfaces. */
    triangles: SubtreeStatus;
    /** Line geometry. */
    lines: SubtreeStatus;
    /** Terrain surfaces. */
    terrain: SubtreeStatus;
    /** Point clouds. */
    points: SubtreeStatus;
    /** 2D documents placed in the scene. */
    documents: SubtreeStatus;
};
/** Name of one geometry kind, e.g. `"triangles"`. */
export type Subtree = keyof Subtrees;

export type CameraStep = { position: vec3; rotation: quat; fov?: number; kind: CameraType };
export type ObjectGroups = { default: ObjectGroup; defaultHidden: ObjectGroup; custom: ObjectGroup[] };

// Redux toolkit with immer removes readonly modifier of state in the reducer so we get ts errors
// unless we cast the types to writable ones.
export type DeepMutable<T> = { -readonly [P in keyof T]: DeepMutable<T[P]> };
export type CameraState =
    | {
          type: CameraType.Orthographic;
          /**
           * If true, lock ortho to the best axis-aligned 2D view and disable ortho mouse rotation.
           */
          fixOrthoCameraToBestAxisAlignedView?: boolean;
          goTo?: {
              position: Camera["position"];
              rotation: Camera["rotation"];
              fov?: number;
              far?: number;
              near?: number;
              flyTime?: number;
          };
          gridOrigo?: vec3;
      }
    | {
          type: CameraType.Pinhole;
          goTo?: {
              position: Camera["position"];
              rotation: Camera["rotation"];
              fov?: number;
              far?: number;
              near?: number;
              flyTime?: number;
          };
          zoomTo?: BoundingSphere;
      };

export type SavedCameraPositions = { currentIndex: number; positions: CameraStep[] };

export enum StampKind {
    LogPoint,
    XsiteManageMachineLocation,
    DitioMachine,
    DitioChecklist,
    Deviation,
    CanvasContextMenu,
    Properties,
    Classification,
}

type LogPointStamp = {
    kind: StampKind.LogPoint;
    data: {
        logPoint: LogPoint;
    };
};

type XsiteManageMachineLocationStamp = {
    kind: StampKind.XsiteManageMachineLocation;
    data: {
        location: MachineLocation;
    };
};

type DitioMachineStamp = {
    kind: StampKind.DitioMachine;
    data: {
        machine: DitioMachine;
    };
};

type DitioChecklistStamp = {
    kind: StampKind.DitioChecklist;
    data: {
        checklist: Checklist;
    };
};

type DeviationStamp = {
    kind: StampKind.Deviation;
    data: {
        deviation: number;
    };
};

type ClassificationStamp = {
    kind: StampKind.Classification;
    data: {
        pointFactor: number;
    };
};

type CanvasContextMenuStamp = {
    kind: StampKind.CanvasContextMenu;
    data: {
        object?: number;
        position?: Vec3;
        normal?: Vec3 | undefined;
    };
};

type PropertiesStamp = {
    kind: StampKind.Properties;
    objectIds: ObjectId[];
    properties: [key: string, value: string][][];
};

export type Stamp = { mouseX: number; mouseY: number; pinned: boolean } & (
    | LogPointStamp
    | XsiteManageMachineLocationStamp
    | DeviationStamp
    | CanvasContextMenuStamp
    | PropertiesStamp
    | DitioMachineStamp
    | DitioChecklistStamp
    | ClassificationStamp
);

export type SceneConfig = Omit<SceneData, "settings" | "customProperties"> & {
    objectGroups: ObjectGroup[];
    settings: Internal.RenderSettingsExt | undefined;
    customProperties: CustomProperties;
    projectId: string;
};

export type CadCamera = { kind: "pinhole" | "orthographic"; position: vec3; rotation: quat; fov: number };

export type LabeledKnot = {
    position: number;
    color: VecRGBA;
    label: string;
};

/**
 * The surface pick captured when the main object was last selected by a canvas click. `objectId` must be
 * checked against the current `mainObject` before use, as it isn't cleared on every selection.
 */
export type MainObjectPick = {
    objectId: number;
    position: [number, number, number];
    normal: [number, number, number];
};

export type ClippingPlane = RenderStateClippingPlane & {
    baseW: number;
    color: VecRGBA;
    showPlane: boolean;
    flipped?: boolean;
    rotation?: number;
    anchorPos?: ReadonlyVec3;
    /**
     * Auto-added when switching to the orthographic camera to reveal a deep selected object. Marked so
     * it can be removed again on switching back to pinhole. Otherwise behaves like a normal user plane.
     */
    temporary?: boolean;
    wBounds?: {
        min: number;
        max: number;
    };
    /**
     * Assigned on create so a late object-bounds lookup can find this plane even if
     * another was added at the same index with the same normal and `baseW`.
     */
    boundsRequestId?: number;
    outline: {
        enabled: boolean;
        color: VecRGB;
        lineColor?: VecRGB;
    };
};
